import React, { useState } from "react";
import { Mic, Users, Church, GraduationCap, Briefcase, ShieldCheck, ArrowRight, CheckCircle2, Phone, Mail, User, Building2, MessageSquare, Calendar } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const FOUNDER_PHOTO = `${process.env.REACT_APP_BACKEND_URL}/api/site-image/founder-depression`;
const TIM_SPEAKING = `${process.env.REACT_APP_BACKEND_URL}/api/site-image/tim-speaking`;

const TOPICS = [
    {
        Icon: ShieldCheck,
        title: "The Mind Is A Battlefield",
        text: "Anchored in 2 Corinthians 10:5 — what Scripture, neuroscience, and lived experience together teach us about taking thoughts captive.",
    },
    {
        Icon: Mic,
        title: "Faith Meets Mental Health",
        text: "Why the Church can't ignore depression and anxiety anymore — and how to fight back without choosing between Bible and brain science.",
    },
    {
        Icon: Briefcase,
        title: "The Battle Drill",
        text: "A practical 4-step framework — Recognize, Reject, Replace, Repeat — that anyone can use the moment a destructive thought hits.",
    },
    {
        Icon: GraduationCap,
        title: "My Story",
        text: "The night I wasn't supposed to survive — and why MindWorks exists. Raw, honest, and built for real people in real pain.",
    },
];

const AUDIENCES = [
    { Icon: Church, label: "Churches & Ministries" },
    { Icon: GraduationCap, label: "Schools & Youth Groups" },
    { Icon: Briefcase, label: "Conferences & Retreats" },
    { Icon: Users, label: "Men's & Women's Groups" },
];

const TAKEAWAYS = [
    "A biblical framework for understanding mental warfare",
    "Practical neuroscience tools they can use the same night",
    "Permission to fight depression and anxiety without shame",
    "A clear next step — not just a feel-good moment",
];

const Speaking = () => {
    const [form, setForm] = useState({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        organization: "",
        event_type: "",
        event_date: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim() || !form.phone.trim() || !form.organization.trim()) {
            toast.error("Please fill out all required fields.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/speaking/request", form);
            setDone(true);
            toast.success("Got it. Tim will personally review and reach out within 48 hours.");
        } catch (err) {
            toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Something glitched. Try again or email hello@mail.wearemindworks.org directly.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-black" data-testid="speaking-page">
            {/* ═══════════════ HERO ═══════════════ */}
            <section className="relative overflow-hidden border-b border-white/10 bg-black">
                {/* Red glow accents */}
                <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-red-700/15 blur-3xl rounded-full pointer-events-none" />
                <div className="absolute -bottom-40 right-0 w-[500px] h-[500px] bg-red-900/25 blur-3xl rounded-full pointer-events-none" />

                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-20 md:pt-28 md:pb-28">
                    <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                        {/* LEFT — Headline + CTA */}
                        <div className="lg:col-span-7">
                            <div className="flex items-center gap-3 mb-8 text-xs tracking-[0.5em] text-red-500 font-display uppercase">
                                <span className="h-px w-16 bg-red-600" />
                                <Mic size={14} />
                                Book Tim To Speak
                            </div>

                            <h1
                                className="font-display uppercase tracking-tighter leading-[0.82] text-5xl sm:text-6xl md:text-7xl lg:text-8xl"
                                data-testid="speaking-headline"
                            >
                                Bring the <br />
                                <span className="text-red-600">battlefield</span> <br />
                                to your <br />
                                people.
                            </h1>

                            <p className="mt-8 max-w-xl text-white/80 text-lg md:text-xl leading-relaxed">
                                Mental health from the pulpit, the platform, or the panel — raw, biblical, and built for the people in your room who are quietly losing the war.
                            </p>

                            <div className="mt-8 flex flex-wrap items-center gap-6 text-white/60 text-sm">
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 size={16} className="text-red-500" />
                                    <span>Faith-rooted</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 size={16} className="text-red-500" />
                                    <span>Science-backed</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 size={16} className="text-red-500" />
                                    <span>Brutally honest</span>
                                </div>
                            </div>

                            <a
                                href="#book"
                                className="mt-10 btn-primary inline-flex text-base"
                                data-testid="speaking-hero-cta"
                            >
                                Request Tim For Your Event <ArrowRight size={18} />
                            </a>
                        </div>

                        {/* RIGHT — Speaking photo card */}
                        <div className="lg:col-span-5">
                            <div className="relative">
                                {/* Offset shadow block for tactical aesthetic */}
                                <div className="absolute -inset-3 bg-red-700/20 -z-10" style={{ transform: "translate(14px, 14px)" }} />
                                <div className="relative border-2 border-red-700/50 bg-black overflow-hidden shadow-[0_0_80px_-20px_rgba(185,28,28,0.6)]">
                                    <img
                                        src={TIM_SPEAKING}
                                        alt="Tim speaking at a MindWorks event"
                                        className="w-full h-auto object-cover block"
                                        data-testid="speaking-hero-photo"
                                    />
                                    {/* Corner stencil tags */}
                                    <div className="absolute top-4 left-4 bg-red-700 px-3 py-1 font-stencil text-white text-[10px] tracking-widest">
                                        ON THE MIC
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/70 to-transparent p-5 pt-12">
                                        <div className="font-stencil text-[10px] tracking-[0.4em] text-red-500 mb-1">FOUNDER · SPEAKER</div>
                                        <div className="font-display text-2xl uppercase tracking-tight">Tim Anguiano</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════════ INVITATION / SYNOPSIS ═══════════════ */}
            <section className="bg-[#050505] py-24 md:py-32 border-b border-white/10">
                <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
                    <div className="flex items-center justify-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                        <span className="h-px w-12 bg-red-600" />
                        The Invitation
                        <span className="h-px w-12 bg-red-600" />
                    </div>

                    <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
                        Why invite Tim <br />
                        <span className="text-red-600">to speak?</span>
                    </h2>

                    <div className="mt-12 max-w-3xl mx-auto space-y-6 text-left">
                        <p className="text-lg md:text-xl text-white/85 leading-relaxed">
                            Most of the people sitting in your pews, conference seats, and youth retreats are quietly fighting battles in their minds nobody sees. Depression. Anxiety. Suicidal thoughts. Shame. The lie that they're too far gone.
                        </p>
                        <p className="text-base md:text-lg text-white/70 leading-relaxed">
                            Tim Anguiano speaks on a <span className="text-white">topic not normally talked about in church</span> — where <span className="text-white">Scripture and neuroscience</span> finally have the same conversation. He's lived the dark night. He's done the clinical work. And he's built a framework that gives people both <span className="text-white">truth and tools</span> they can use the same night they leave your event.
                        </p>
                        <p className="text-base md:text-lg text-white/70 leading-relaxed">
                            If you want a speaker who will tell your audience the truth about the war in their mind — and then hand them a battle plan to win it — Tim is built for your stage.
                        </p>
                    </div>
                </div>
            </section>

            {/* ═══════════════ TOPICS ═══════════════ */}
            <section className="bg-black py-24 md:py-32 border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                        <div>
                            <div className="flex items-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                                <span className="h-px w-12 bg-red-600" />
                                What Tim Speaks On
                            </div>
                            <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
                                Topics from <br />
                                <span className="text-red-600">the front.</span>
                            </h2>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
                        {TOPICS.map((t, i) => (
                            <div
                                key={t.title}
                                className="group bg-black p-8 md:p-10 hover:bg-[#0a0a0a] transition-colors"
                                data-testid={`speaking-topic-${i}`}
                            >
                                <div className="flex items-start justify-between mb-6">
                                    <div className="bg-red-700/15 border border-red-700/40 p-3">
                                        <t.Icon size={26} className="text-red-600" strokeWidth={1.5} />
                                    </div>
                                    <span className="font-display text-white/15 text-5xl leading-none">0{i + 1}</span>
                                </div>
                                <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight leading-tight group-hover:text-red-600 transition-colors">
                                    {t.title}
                                </h3>
                                <p className="mt-4 text-white/70 leading-relaxed">{t.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════════════ AUDIENCES + TAKEAWAYS ═══════════════ */}
            <section className="bg-[#050505] py-24 md:py-32 border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-20">
                    {/* AUDIENCES */}
                    <div>
                        <div className="flex items-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                            <span className="h-px w-12 bg-red-600" />
                            Built For
                        </div>
                        <h3 className="font-display text-3xl md:text-5xl uppercase tracking-tight leading-[0.95]">
                            Who Tim <br />
                            <span className="text-red-600">speaks to.</span>
                        </h3>
                        <div className="mt-10 grid sm:grid-cols-2 gap-4">
                            {AUDIENCES.map((a) => (
                                <div
                                    key={a.label}
                                    className="border border-white/10 bg-black p-5 flex items-center gap-4 hover:border-red-700/50 transition-colors"
                                    data-testid={`audience-${a.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                                >
                                    <a.Icon size={22} className="text-red-600 flex-shrink-0" strokeWidth={1.5} />
                                    <span className="font-display text-sm uppercase tracking-tight">{a.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* TAKEAWAYS */}
                    <div>
                        <div className="flex items-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                            <span className="h-px w-12 bg-red-600" />
                            Your Audience Leaves With
                        </div>
                        <h3 className="font-display text-3xl md:text-5xl uppercase tracking-tight leading-[0.95]">
                            What they <br />
                            <span className="text-red-600">walk out with.</span>
                        </h3>
                        <ul className="mt-10 space-y-4">
                            {TAKEAWAYS.map((t, i) => (
                                <li
                                    key={i}
                                    className="flex items-start gap-4 border-l-2 border-red-600 pl-5 py-2 bg-black"
                                    data-testid={`takeaway-${i}`}
                                >
                                    <span className="font-stencil text-[10px] tracking-widest text-red-600 pt-1.5 flex-shrink-0">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="text-white/85 leading-relaxed">{t}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* ═══════════════ BOOK / REQUEST FORM ═══════════════ */}
            <section className="bg-black py-24 md:py-32 relative overflow-hidden" id="book">
                <div className="absolute -top-32 right-0 w-[500px] h-[500px] bg-red-700/10 blur-3xl rounded-full pointer-events-none" />

                <div className="relative max-w-3xl mx-auto px-6 lg:px-10">
                    <div className="text-center">
                        <div className="flex items-center justify-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                            <span className="h-px w-12 bg-red-600" />
                            Request A Date
                            <span className="h-px w-12 bg-red-600" />
                        </div>
                        <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
                            Let's bring <br />
                            <span className="text-red-600">the truth</span> <br />
                            to your stage.
                        </h2>
                        <p className="mt-6 text-white/65 leading-relaxed max-w-xl mx-auto">
                            Fill this out and Tim will personally review your request and reach out within 48 hours.
                        </p>
                    </div>

                    {/* Form Card */}
                    <div className="mt-16 border-2 border-red-700/50 bg-[#050505] p-8 md:p-12 shadow-[0_0_80px_-20px_rgba(185,28,28,0.6)]">
                        {done ? (
                            <div className="text-center py-8" data-testid="speaking-success">
                                <div className="mx-auto w-16 h-16 border-2 border-red-600 flex items-center justify-center">
                                    <CheckCircle2 size={32} className="text-red-500" />
                                </div>
                                <h3 className="mt-6 font-display text-3xl md:text-4xl uppercase tracking-tight">
                                    Request received.
                                </h3>
                                <p className="mt-4 text-white/70 leading-relaxed max-w-md mx-auto">
                                    Tim personally reads every speaking request. Expect a reply within <span className="text-white">48 hours</span> to the email you provided.
                                </p>
                                <p className="mt-4 text-white/50 text-sm">
                                    In the meantime — feel free to share the assessment with your team so they can preview what Tim teaches.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={submit} data-testid="speaking-form" className="space-y-5">
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <Field icon={User} label="First Name" required>
                                        <input
                                            type="text"
                                            placeholder="Jane"
                                            value={form.first_name}
                                            onChange={update("first_name")}
                                            required
                                            className={inputCls}
                                            data-testid="speaking-first-name"
                                        />
                                    </Field>
                                    <Field icon={User} label="Last Name" required>
                                        <input
                                            type="text"
                                            placeholder="Doe"
                                            value={form.last_name}
                                            onChange={update("last_name")}
                                            required
                                            className={inputCls}
                                            data-testid="speaking-last-name"
                                        />
                                    </Field>
                                </div>

                                <Field icon={Mail} label="Email" required>
                                    <input
                                        type="email"
                                        placeholder="you@church.org"
                                        value={form.email}
                                        onChange={update("email")}
                                        required
                                        className={inputCls}
                                        data-testid="speaking-email"
                                    />
                                </Field>

                                <Field icon={Phone} label="Phone Number" required>
                                    <input
                                        type="tel"
                                        placeholder="(555) 123-4567"
                                        value={form.phone}
                                        onChange={update("phone")}
                                        required
                                        className={inputCls}
                                        data-testid="speaking-phone"
                                    />
                                </Field>

                                <Field icon={Building2} label="Organization / Church" required>
                                    <input
                                        type="text"
                                        placeholder="Castle Rock Community Church"
                                        value={form.organization}
                                        onChange={update("organization")}
                                        required
                                        className={inputCls}
                                        data-testid="speaking-organization"
                                    />
                                </Field>

                                <div className="grid sm:grid-cols-2 gap-5">
                                    <Field icon={Calendar} label="Event Date (if known)">
                                        <input
                                            type="text"
                                            placeholder="Spring 2026"
                                            value={form.event_date}
                                            onChange={update("event_date")}
                                            className={inputCls}
                                            data-testid="speaking-date"
                                        />
                                    </Field>
                                    <Field icon={Briefcase} label="Type Of Event">
                                        <input
                                            type="text"
                                            placeholder="Sunday service / Conference / Retreat"
                                            value={form.event_type}
                                            onChange={update("event_type")}
                                            className={inputCls}
                                            data-testid="speaking-event-type"
                                        />
                                    </Field>
                                </div>

                                <Field icon={MessageSquare} label="Tell Tim About Your Event">
                                    <textarea
                                        rows={5}
                                        placeholder="Audience size, topic interest, the vision behind the event..."
                                        value={form.message}
                                        onChange={update("message")}
                                        className={`${inputCls} resize-none`}
                                        data-testid="speaking-message"
                                    />
                                </Field>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="btn-primary w-full text-base justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                                    data-testid="speaking-submit"
                                >
                                    {loading ? "Sending..." : "Send Speaking Request"} <ArrowRight size={18} />
                                </button>

                                <p className="text-white/40 text-xs text-center">
                                    Required: First Name · Last Name · Email · Phone · Organization
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};

const inputCls = "w-full px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30 transition-colors";

const Field = ({ icon: Icon, label, required, children }) => (
    <label className="block">
        <div className="flex items-center gap-2 mb-2 text-xs font-display tracking-[0.2em] uppercase text-white/70">
            {Icon && <Icon size={14} className="text-red-600" strokeWidth={1.8} />}
            {label}
            {required && <span className="text-red-500">*</span>}
        </div>
        {children}
    </label>
);

export default Speaking;
